import React from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';

import ApplyLeaveModal from '../../Admin/AttendanceViolation/components/ApplyLeaveModal';
import ViolationRowActions from '../../Admin/AttendanceViolation/components/ViolationRowActions';
import ViolationTimeMeta from '../../Admin/AttendanceViolation/components/ViolationTimeMeta';
import ViolationTypeChip from '../../Admin/AttendanceViolation/components/ViolationTypeChip';
import ViolationTimeLegend from '../../Admin/AttendanceViolation/components/ViolationTimeLegend';
import {
  getSortedViolations,
  getSummaryRowId,
  getViolationActionConfig,
  getViolationEmployeeName,
  getViolationEmployeeSubtext,
  getWarningStatusConfig,
  isViolationLeaveApplied,
  mergeViolationForApplyLeave,
  violationDateNumToIstStartMs
} from '../../../utils/attendanceViolationHelpers';

import '../../Admin/AttendanceViolation/AttendanceViolation.scss';

const StatusBadge = ({ status }) => {
  const config = getWarningStatusConfig(status);
  return (
    <span className={`att-violation-status att-violation-status--${config.color}`}>
      {config.label}
    </span>
  );
};

const ViolationDateBadge = ({ dateNum, typeClass }) => {
  const ms = violationDateNumToIstStartMs(dateNum);
  if (!ms) return null;
  const m = moment(ms).utcOffset(330);

  return (
    <div className={`att-violation-date-badge att-violation-date-badge--${typeClass || 'default'}`}>
      <span className="att-violation-date-badge__dow">{m.format('ddd')}</span>
      <span className="att-violation-date-badge__day">{m.format('DD')}</span>
      <span className="att-violation-date-badge__month">{m.format('MMM')}</span>
    </div>
  );
};

const ViolationTimeline = ({ userRow, summary, onApplyLeave, onActionSuccess }) => {
  const violations = getSortedViolations(summary);
  const typeClass = summary?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';

  if (!violations.length) {
    return <p className="att-violation-history__empty">No violation dates found.</p>;
  }

  return (
    <div className={`att-violation-timeline att-violation-timeline--${typeClass}`}>
      {violations.map((violation, index) => {
        const isPending = violation?.violationId === summary?.pendingViolationId;
        const actionConfig = getViolationActionConfig(violation);

        return (
          <div
            key={violation?.violationId || `${violation?.date}-${violation?.monthlyCount}`}
            className={`att-violation-timeline__item${
              isPending ? ' att-violation-timeline__item--pending' : ''
            }`}
          >
            <div className="att-violation-timeline__rail" aria-hidden>
              <span
                className={`att-violation-timeline__dot att-violation-timeline__dot--${actionConfig.color}`}
              />
              {index < violations.length - 1 ? (
                <span className="att-violation-timeline__line" />
              ) : null}
            </div>

            <div className="att-violation-timeline__card">
              <ViolationDateBadge dateNum={violation?.date} typeClass={typeClass} />
              <div className="att-violation-timeline__content">
                <span className="att-violation-timeline__count">
                  Violation #{violation?.monthlyCount ?? '—'}
                </span>
                <ViolationTimeMeta violation={violation} />
              </div>
              <div className="att-violation-timeline__actions">
                <ViolationRowActions
                  violation={violation}
                  summary={summary}
                  onApplyLeave={() => onApplyLeave(userRow, summary, violation)}
                  onActionSuccess={onActionSuccess}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ViolationSummarySection = ({ userRow, summary, onApplyLeave, onActionSuccess }) => {
  const violations = getSortedViolations(summary);
  const leaveApplied = isViolationLeaveApplied(summary);
  const typeClass = summary?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';

  return (
    <section className={`att-violation-summary att-violation-summary--${typeClass}`}>
      <div className="att-violation-summary__head">
        <div className="att-violation-summary__title-wrap">
          <ViolationTypeChip
            violationType={summary?.violationType}
            count={summary?.monthlyViolationCount ?? violations.length}
          />
        </div>
        {leaveApplied ? (
          <div className="att-violation-summary__actions">
            <span className="att-violation-action att-violation-action--done">
              <i className="bi bi-check-circle-fill" aria-hidden /> Leave applied
            </span>
          </div>
        ) : null}
      </div>
      <ViolationTimeline
        userRow={userRow}
        summary={summary}
        onApplyLeave={onApplyLeave}
        onActionSuccess={onActionSuccess}
      />
    </section>
  );
};

const ViolationUserDetailModal = ({ show, userRow, onClose, onLeaveApplied }) => {
  const [applyTarget, setApplyTarget] = React.useState(null);
  const summaries = userRow?.violationSummaries || [];
  const profilePicture = userRow?.user?.profilePicture;
  const totalViolations = userRow?.totalViolationsThisMonth ?? 0;

  const handleApplyLeave = (row, summary, violation) => {
    setApplyTarget(mergeViolationForApplyLeave(row, summary, violation));
  };

  const handleClose = () => {
    setApplyTarget(null);
    onClose?.();
  };

  const handleLeaveSuccess = () => {
    setApplyTarget(null);
    onLeaveApplied?.();
  };

  if (!userRow) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        className="att-violation-widget-detail-modal"
        scrollable
      >
        <Modal.Header closeButton className="att-violation-widget-detail-modal__head">
          <div className="att-violation-widget-detail-modal__user">
            <div className="att-violation-widget__avatar-wrap">
              {profilePicture ? (
                <img className="att-violation-widget__avatar" src={profilePicture} alt="" />
              ) : (
                <span className="att-violation-widget__avatar att-violation-widget__avatar--placeholder">
                  {(getViolationEmployeeName(userRow) || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              {totalViolations > 0 ? (
                <span className="att-violation-widget__avatar-count">{totalViolations}</span>
              ) : null}
            </div>
            <div>
              <Modal.Title className="att-violation-widget-detail-modal__title">
                {getViolationEmployeeName(userRow)}
              </Modal.Title>
              <p className="att-violation-widget-detail-modal__role">
                {getViolationEmployeeSubtext(userRow)}
              </p>
            </div>
          </div>
          <StatusBadge status={userRow?.warningStatus} />
        </Modal.Header>

        <Modal.Body className="att-violation-widget-detail-modal__body">
          <ViolationTimeLegend compact className="att-violation-widget-detail-modal__legend" />
          <div className="att-violation-summaries">
            {summaries.map((summary) => (
              <ViolationSummarySection
                key={getSummaryRowId(userRow, summary)}
                userRow={userRow}
                summary={summary}
                onApplyLeave={handleApplyLeave}
                onActionSuccess={onLeaveApplied}
              />
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <ApplyLeaveModal
        show={Boolean(applyTarget)}
        violation={applyTarget}
        onClose={() => setApplyTarget(null)}
        onSuccess={handleLeaveSuccess}
      />
    </>
  );
};

export default ViolationUserDetailModal;
